/**
 * Shared "same design group" suppression for setup fee display (Order Details, etc.)
 * Logic mirrors CartSummary / Cart.vue applySuppressionToSetupFeeDetails + grouping.
 */
import {
    computeFingerprintFromArbitraryPayload,
    stampImprintLocationsFromCanvasStore
} from "@/common/suppressDesignGroupFingerprint";

export function normalizeImprintLocationKey(locationName) {
    const raw = (locationName || "").toLowerCase().trim();
    if (raw.includes("front")) return "front";
    if (raw.includes("back")) return "back";
    if (raw.includes("left")) return "left";
    if (raw.includes("right")) return "right";
    return "";
}

export function getEffectiveImprintLocations(item, parentItem = null) {
    const parseImprintLocations = (locations) => {
        if (Array.isArray(locations)) return locations;
        if (typeof locations === "string") {
            try {
                const parsed = JSON.parse(locations);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                return [];
            }
        }
        return [];
    };

    const itemLocations = parseImprintLocations(item && item.imprint_locations);
    if (itemLocations.length > 0) {
        return itemLocations;
    }

    if (parentItem && parentItem.imprint_locations) {
        const parentLocations = parseImprintLocations(
            parentItem.imprint_locations
        );
        if (parentLocations.length > 0) {
            return parentLocations;
        }
    }

    return [];
}

export function getSuppressAdditionalSetupRunChargeConfig(
    item,
    parentItem = null
) {
    const parseDecoration = (target) => {
        if (!target || !target.decoration) return null;
        if (typeof target.decoration === "string") {
            try {
                return JSON.parse(target.decoration);
            } catch (e) {
                return null;
            }
        }
        return target.decoration;
    };
    const decoration = parseDecoration(item) || parseDecoration(parentItem);
    const masterService =
        decoration && decoration.master_service
            ? decoration.master_service
            : {};
    const productMasterService =
        decoration && decoration.product_master_service
            ? decoration.product_master_service
            : {};
    const enabled =
        Number(
            productMasterService.suppress_additional_setup_run_charge ||
                masterService.suppress_additional_setup_run_charge ||
                0
        ) === 1;
    return { enabled, masterService };
}

/** Vendor / PDP master_service row: suppress additional setup & run charge flag. */
export function readSuppressFlagFromServiceMaster(masterService) {
    if (!masterService) return false;
    const raw = masterService.suppress_additional_setup_run_charge;
    return (
        Number(raw) === 1 || raw === true || raw === "1" || raw === 1
    );
}

/** PDP grouping key: 1, 2, 3, or full_color — ink colors are not compared on product details. */
export function getImprintLocationColorCountKey(location) {
    if (!location || location.is_selected === false) return "";
    const noc = location.no_of_color;
    if (
        location.is_full_color_selected === true ||
        location.is_full_color === true ||
        noc === "full_color" ||
        String(noc).toLowerCase() === "full_color"
    ) {
        return "full_color";
    }
    const n = parseInt(noc, 10);
    if (!Number.isNaN(n) && n > 0) return String(n);
    return "";
}

/**
 * PDP only: suppress this location only if another selected location already
 * claimed the same color-count key (1+1 → second is $0; 1 + full_color → both charge).
 */
export function isImprintLocationSuppressedByColorCountOnly(
    imprintLocations,
    location
) {
    if (!location || location.is_selected === false) return false;
    const targetKey = getImprintLocationColorCountKey(location);
    if (!targetKey) return false;
    let firstForKey = null;
    for (const loc of imprintLocations || []) {
        if (!loc || !loc.is_selected) continue;
        const k = getImprintLocationColorCountKey(loc);
        if (k !== targetKey) continue;
        if (!firstForKey) {
            firstForKey = loc;
            continue;
        }
        if (loc === location) {
            return true;
        }
    }
    return false;
}

/**
 * Same color count → same group; first selected location with that count is primary.
 * @returns {Array<{location: object, key: string, isGroupPrimary: boolean}>}
 */
export function getLocationGroupingMetadataByColorCountOnly(imprintLocations) {
    const selected = (imprintLocations || []).filter(
        (loc) => loc && loc.is_selected
    );
    const seenKeys = new Set();
    return selected.map((location) => {
        const key =
            getImprintLocationColorCountKey(location) ||
            `loc_${String(location.location || "")
                .toLowerCase()
                .trim()}`;
        const isGroupPrimary = !seenKeys.has(key);
        if (isGroupPrimary) {
            seenKeys.add(key);
        }
        return { location, key, isGroupPrimary };
    });
}

/** PDP: suppress math only when service flag is on and user selected 2+ imprint areas. */
export function isPdpColorCountSuppressEffective(
    imprintLocations,
    suppressEnabledFromService
) {
    if (!suppressEnabledFromService) return false;
    const selected = (imprintLocations || []).filter(
        (loc) => loc && loc.is_selected
    );
    return selected.length >= 2;
}

function getLocationColorSignature(location) {
    if (!location || !Array.isArray(location.colors)) return "";
    const signature = location.colors
        .map((entry) => {
            const color = entry && entry.color ? entry.color : {};
            return String(
                color.color_id ||
                    color.id ||
                    color.hex ||
                    color.color_value ||
                    color.name ||
                    ""
            ).toLowerCase();
        })
        .filter(Boolean)
        .sort();
    return signature.join("|");
}

function getLocationDesignComparablePayload(location) {
    if (!location || typeof location !== "object") return {};
    const payload =
        location.design_data ||
        location.design_json ||
        location.artwork_data ||
        location.canvas_json ||
        location.objects ||
        location.print_data ||
        {};
    let normalizedPayload = payload;
    if (typeof normalizedPayload === "string") {
        try {
            normalizedPayload = JSON.parse(normalizedPayload);
        } catch (e) {
            normalizedPayload = { raw: normalizedPayload };
        }
    }
    const skipKeys = new Set([
        "left",
        "top",
        "x",
        "y",
        "angle",
        "rotate",
        "rotation",
        "scaleX",
        "scaleY",
        "translateX",
        "translateY",
        "posX",
        "posY"
    ]);
    const normalizeNode = (node) => {
        if (Array.isArray(node)) return node.map(normalizeNode);
        if (!node || typeof node !== "object") return node;
        return Object.keys(node)
            .sort()
            .reduce((acc, key) => {
                if (skipKeys.has(key)) return acc;
                acc[key] = normalizeNode(node[key]);
                return acc;
            }, {});
    };
    return normalizeNode(normalizedPayload);
}

function getLocationComparisonKey(location) {
    const colorSignature = getLocationColorSignature(location);
    const designPayload = getLocationDesignComparablePayload(location);
    const fp =
        (location.suppress_design_fingerprint &&
            String(location.suppress_design_fingerprint)) ||
        computeFingerprintFromArbitraryPayload(
            location.design_data ||
                location.design_json ||
                location.artwork_data ||
                location.canvas_json ||
                location.objects ||
                location.print_data
        );
    const designIdentity =
        location.design_id ||
        location.saved_design_id ||
        location.artwork_id ||
        location.image_url ||
        location.artwork_url ||
        "";
    const payloadSignature = JSON.stringify(designPayload || {});
    return [
        colorSignature,
        String(designIdentity),
        String(fp),
        payloadSignature
    ].join("::");
}

export function getLocationGroupingMetadata(
    imprintLocations,
    suppressionEnabled
) {
    const selected = (imprintLocations || []).filter(
        (loc) => loc && loc.is_selected
    );
    const hasPresetGrouping = selected.some(
        (location) =>
            location.design_group_id !== undefined &&
            location.design_group_id !== null &&
            location.design_group_id !== ""
    );
    if (suppressionEnabled && hasPresetGrouping) {
        const seenPreset = new Set();
        return selected.map((location, index) => {
            const presetKey = String(
                location.design_group_id || `preset_${index}`
            );
            const derivedPrimary =
                location.is_group_primary !== undefined
                    ? !!location.is_group_primary
                    : !seenPreset.has(presetKey);
            if (derivedPrimary) {
                seenPreset.add(presetKey);
            }
            return {
                location,
                key: presetKey,
                isGroupPrimary: derivedPrimary
            };
        });
    }
    if (!suppressionEnabled) {
        return selected.map((location, index) => ({
            location,
            key: `legacy_${index}`,
            isGroupPrimary: index === 0
        }));
    }
    const seenKeys = new Set();
    return selected.map((location) => {
        const key = getLocationComparisonKey(location);
        const isGroupPrimary = !seenKeys.has(key);
        if (isGroupPrimary) {
            seenKeys.add(key);
        }
        return {
            location,
            key,
            isGroupPrimary
        };
    });
}

/**
 * Stamps canvas fingerprints, trims empty color rows, assigns design_group_id / is_group_primary
 * using {@link getLocationGroupingMetadata} (same comparison key as cart setup tooltip — includes
 * normalized design payload, not just the 3-part studio key).
 * Mutates imprintLocations in place.
 */
export function prepareImprintLocationsForSuppressCart(
    imprintLocations,
    suppressEnabled,
    canvasStore
) {
    if (!Array.isArray(imprintLocations)) return;
    try {
        stampImprintLocationsFromCanvasStore(
            imprintLocations,
            canvasStore && typeof canvasStore === "object" ? canvasStore : {}
        );
    } catch (e) {
        /* ignore */
    }
    imprintLocations.forEach((loc) => {
        loc.colors = (loc.colors || []).filter(
            (lcolor) => lcolor && lcolor.color
        );
        loc.suppress_additional_setup_run_charge = suppressEnabled ? 1 : 0;
        if (!loc.is_selected) {
            loc.design_group_id = null;
            loc.is_group_primary = false;
        }
    });
    const grouping = getLocationGroupingMetadata(
        imprintLocations,
        suppressEnabled
    );
    grouping.forEach((entry) => {
        entry.location.design_group_id =
            entry.key !== undefined && entry.key !== null
                ? String(entry.key)
                : null;
        entry.location.is_group_primary = !!entry.isGroupPrimary;
    });
    imprintLocations.forEach((loc) => {
        if (!loc.is_selected) {
            loc.design_group_id = null;
            loc.is_group_primary = false;
        }
    });
}

export function parseSetupFeesDetailsForTotal(setupFeeDetails) {
    if (setupFeeDetails == null || setupFeeDetails === "") return null;
    if (typeof setupFeeDetails === "object") return setupFeeDetails;
    if (typeof setupFeeDetails === "string") {
        try {
            return JSON.parse(setupFeeDetails);
        } catch (e) {
            return null;
        }
    }
    return null;
}

/** Catalog total from stored JSON (no imprint-based suppression). */
export function getRawSetupFeesDetailsTotal(setupFeeDetails) {
    const d = parseSetupFeesDetailsForTotal(setupFeeDetails);
    if (!d) return 0;
    const headerTotal = parseFloat(d.total);
    if (Array.isArray(d.locations) && d.locations.length > 0) {
        const sumLocs = d.locations.reduce(
            (s, loc) => s + parseFloat((loc && loc.total) || 0),
            0
        );
        if (!Number.isNaN(headerTotal) && headerTotal > 0) {
            return headerTotal;
        }
        return sumLocs;
    }
    return Number.isNaN(headerTotal) ? 0 : headerTotal;
}

const SETUP_FEE_TOTAL_TOL = 0.021;

/**
 * When setup_fee (column) reflects full catalog but setup_fees_details still has suppressed
 * rows ($0 / is_suppressed_by_group), rebuild location totals so the breakdown matches the column.
 */
export function expandSetupFeeDetailsToMatchTargetTotal(
    setupFeeDetails,
    targetTotal
) {
    if (setupFeeDetails == null || setupFeeDetails === "") return null;
    let d;
    try {
        d =
            typeof setupFeeDetails === "string"
                ? JSON.parse(setupFeeDetails)
                : JSON.parse(JSON.stringify(setupFeeDetails));
    } catch (e) {
        return null;
    }
    if (!d || !Array.isArray(d.locations) || d.locations.length === 0) {
        return null;
    }

    const target = parseFloat(targetTotal);
    if (Number.isNaN(target) || target <= SETUP_FEE_TOTAL_TOL) {
        return d;
    }

    let sum = d.locations.reduce(
        (s, r) => s + parseFloat((r && r.total) || 0),
        0
    );
    if (Math.abs(sum - target) < SETUP_FEE_TOTAL_TOL) {
        d.total = target;
        return d;
    }
    if (target < sum - SETUP_FEE_TOTAL_TOL) {
        return d;
    }

    const fillIdx = [];
    d.locations.forEach((r, i) => {
        const t = parseFloat((r && r.total) || 0);
        if (
            (r && r.is_suppressed_by_group === true) ||
            t < SETUP_FEE_TOTAL_TOL
        ) {
            fillIdx.push(i);
        }
    });
    if (fillIdx.length === 0) {
        return d;
    }

    let sumPositive = 0;
    d.locations.forEach((r) => {
        const t = parseFloat((r && r.total) || 0);
        if (t > SETUP_FEE_TOTAL_TOL) {
            sumPositive += t;
        }
    });

    const remaining = target - sumPositive;
    if (remaining < SETUP_FEE_TOTAL_TOL) {
        d.total = sumPositive;
        return d;
    }

    const eachLineTotal = remaining / fillIdx.length;

    fillIdx.forEach((idx) => {
        const row = d.locations[idx];
        const q = parseInt(row.quantity, 10);
        const qty = Number.isNaN(q) || q < 1 ? 1 : q;
        const per = eachLineTotal / qty;
        d.locations[idx] = {
            ...row,
            per_price: per,
            amt: eachLineTotal,
            total: eachLineTotal,
            is_suppressed_by_group: false
        };
    });

    sum = d.locations.reduce((s, r) => s + parseFloat((r && r.total) || 0), 0);
    d.total = sum;
    return d;
}

/**
 * Assembly parent after /update-assembly-parent-setup carries the winning child's setup_fees_details.
 * Skip re-zeroing rows from the parent's imprint_locations when that stored total matches the
 * group's max raw catalog total (so tooltip matches DB / combined line).
 */
export function shouldSkipSetupFeeSuppressionForAssemblyParentFromGrouped(
    item,
    parsedSetupDetails,
    groupedItems
) {
    if (!item || !parsedSetupDetails || !Array.isArray(groupedItems)) {
        return false;
    }
    if (groupedItems.length < 2) return false;
    // Header row is groupedItems[0] (cart merge puts assembly parent first). Do not rely only on
    // assembly_parent — some payloads omit it while subItems + group_assembly_id are present.
    const isAssemblyHeaderRow =
        (groupedItems.length > 0 && groupedItems[0] === item) ||
        parseInt(item.assembly_parent, 10) === 1;
    if (!isAssemblyHeaderRow) return false;

    let maxRaw = 0;
    groupedItems.forEach((gi) => {
        if (!gi) return;
        const jsonT = getRawSetupFeesDetailsTotal(gi.setup_fees_details);
        const col = parseFloat(gi.setup_fee || 0) || 0;
        const t = Math.max(jsonT, col);
        if (t > maxRaw) maxRaw = t;
    });

    const parentRaw = getRawSetupFeesDetailsTotal(parsedSetupDetails);
    if (maxRaw <= 0 || parentRaw <= 0) return false;
    return Math.abs(parentRaw - maxRaw) < 0.021;
}

export function isSuppressDesignGroupingEnabledForCartLine(cartLine) {
    return getSuppressAdditionalSetupRunChargeConfig(cartLine).enabled;
}

function flattenOrderLinesForAssemblyGroup(orderItems) {
    const out = [];
    (orderItems || []).forEach((row) => {
        if (!row) return;
        out.push(row);
        (row.assemblySubItems || []).forEach((sub) => {
            if (sub) out.push(sub);
        });
    });
    return out;
}

/**
 * Order/cart display: mixed assembly (suppress + non-suppress) keeps full-catalog setup_fees_details.
 */
export function shouldSkipSetupFeeSuppressionForMixedAssembly(orderItems, item) {
    if (!item) return false;
    const gid = item.group_assembly_id;
    if (
        gid == null ||
        gid === "" ||
        String(gid) === "null" ||
        String(gid) === "undefined"
    ) {
        return false;
    }
    const peers = flattenOrderLinesForAssemblyGroup(orderItems).filter(
        (line) => String(line.group_assembly_id) === String(gid)
    );
    if (peers.length === 0) return false;
    return peers.some((p) => !isSuppressDesignGroupingEnabledForCartLine(p));
}

function resolveImprintForSetupFeeLocation(locationItem, imprintLocs) {
    const locKey = normalizeImprintLocationKey(
        locationItem.name || locationItem.location
    );
    if (!locKey || !Array.isArray(imprintLocs)) return null;
    return (
        imprintLocs.find((l) => {
            if (!l || l.is_selected === false) return false;
            return normalizeImprintLocationKey(l.location) === locKey;
        }) || null
    );
}

/** Setup-fee catalog qty uses 1 for full color — not ink count (full_color_count). */
export function isFullColorSetupFeeLocation(locationItem, imprintLocs = []) {
    if (
        locationItem.is_full_color === true ||
        /full\s*color/i.test(
            String(locationItem.name || locationItem.location || "")
        )
    ) {
        return true;
    }
    const imprint = resolveImprintForSetupFeeLocation(
        locationItem,
        imprintLocs
    );
    if (!imprint) return false;
    const noc = imprint.no_of_color;
    return (
        imprint.is_full_color === true ||
        imprint.is_full_color_selected === true ||
        noc === "full_color" ||
        String(noc).toLowerCase() === "full_color"
    );
}

/**
 * Catalog qty for setup-fee rows (including suppressed): imprint colors, stored qty, or primary sibling.
 */
export function getSetupFeeLocationCatalogQuantity(
    locationItem,
    imprintLocs = [],
    allLocations = null
) {
    if (!locationItem) return 1;

    if (isFullColorSetupFeeLocation(locationItem, imprintLocs)) {
        return 1;
    }

    if (
        locationItem.catalog_quantity != null &&
        locationItem.catalog_quantity !== ""
    ) {
        const cq = parseInt(locationItem.catalog_quantity, 10);
        if (!Number.isNaN(cq) && cq > 0) return cq;
    }
    const q = parseInt(locationItem.quantity, 10);
    if (!Number.isNaN(q) && q > 0) return q;
    const per = parseFloat(locationItem.per_price || 0);
    const tot = parseFloat(locationItem.total || 0);
    if (per > 0 && tot > 0) {
        const derived = Math.round(tot / per);
        if (derived > 0) return derived;
    }
    const imprint = resolveImprintForSetupFeeLocation(
        locationItem,
        imprintLocs
    );
    if (imprint) {
        if (isFullColorSetupFeeLocation(locationItem, [imprint])) {
            return 1;
        }
        const colors = Array.isArray(imprint.colors)
            ? imprint.colors.filter((c) => c && (c.color || c.name))
            : [];
        if (colors.length > 0) return colors.length;
        const noc = parseInt(imprint.no_of_color, 10);
        if (!Number.isNaN(noc) && noc > 0) return noc;
    }
    if (Array.isArray(allLocations)) {
        const primary = allLocations.find(
            (l) =>
                l &&
                l.is_suppressed_by_group !== true &&
                parseInt(l.quantity, 10) > 0
        );
        if (primary) {
            const pq = parseInt(primary.quantity, 10);
            if (!Number.isNaN(pq) && pq > 0) return pq;
        }
    }
    return 1;
}

/**
 * @param {function(object, object): array} [resolveImprintLocations] — e.g. Cart’s getEffectiveImprintLocations (assembly-aware)
 * @returns {object|null} cloned setup_fees_details with suppressed locations zeroed, or original
 */
export function applySuppressionToSetupFeeDetails(
    setupFeeDetails,
    item,
    parentItem = null,
    resolveImprintLocations = null
) {
    if (
        !setupFeeDetails ||
        !setupFeeDetails.locations ||
        !Array.isArray(setupFeeDetails.locations)
    ) {
        return setupFeeDetails;
    }
    const suppressConfig = getSuppressAdditionalSetupRunChargeConfig(
        item,
        parentItem
    );
    if (!suppressConfig.enabled) {
        return setupFeeDetails;
    }
    const imprintLocs = resolveImprintLocations
        ? resolveImprintLocations(item, parentItem) || []
        : getEffectiveImprintLocations(item, parentItem);
    const grouping = getLocationGroupingMetadata(imprintLocs, true);
    const groupingByLocation = {};
    grouping.forEach((entry) => {
        const key = normalizeImprintLocationKey(entry.location.location);
        if (key && groupingByLocation[key] === undefined) {
            groupingByLocation[key] = entry;
        }
    });
    const cloned = JSON.parse(JSON.stringify(setupFeeDetails));
    let grandTotal = 0;
    cloned.locations = cloned.locations.map((locationItem) => {
        const locKey = normalizeImprintLocationKey(
            locationItem.name || locationItem.location
        );
        const groupEntry = locKey ? groupingByLocation[locKey] : null;
        const isSuppressed = groupEntry && !groupEntry.isGroupPrimary;
        if (!isSuppressed) {
            grandTotal += parseFloat(locationItem.total || 0);
            return locationItem;
        }
        const catalogQty = getSetupFeeLocationCatalogQuantity(
            locationItem,
            imprintLocs,
            cloned.locations
        );
        return {
            ...locationItem,
            catalog_quantity: catalogQty,
            quantity: catalogQty,
            per_price: 0,
            total: 0,
            is_suppressed_by_group: true
        };
    });
    cloned.total = grandTotal;
    return cloned;
}

/**
 * Admin Order Details cost breakdown — align with storefront ToolTip run-charge visibility.
 * @param {number|string|null} decorationType
 */
export function shouldShowRunChargeBreakdownRow(row, decorationType) {
    if (!row) return false;
    if (row.is_suppressed_by_group === true) {
        return true;
    }

    const isInclOrDash =
        row.total === "INCL" ||
        row.total === "-" ||
        row.amt === "INCL" ||
        row.amt === "-";

    const numAmt = parseFloat(row.amt);
    const numTotal = parseFloat(row.total);
    const hasPositiveNumeric =
        (!Number.isNaN(numAmt) && numAmt > 0) ||
        (!Number.isNaN(numTotal) && numTotal > 0);

    const isZeroDetailFeeRow =
        (row.type === "fullColor" ||
            row.type === "colorUpcharge" ||
            row.type === "customColorUpcharge" ||
            row.type === "addlColor") &&
        !Number.isNaN(numAmt) &&
        !Number.isNaN(numTotal) &&
        numAmt === 0 &&
        numTotal === 0;

    const rowName = String(row.name || "").toLowerCase();
    const isFirstOrFullColorRow =
        rowName.includes("1st color") ||
        rowName.includes("first color") ||
        rowName.includes("full color");
    const dec = parseInt(decorationType, 10);
    const isEmbroideryDecoration = dec === 2 || dec === 4;

    if (isEmbroideryDecoration && isFirstOrFullColorRow) {
        return (
            isInclOrDash ||
            row.isIncluded === true ||
            hasPositiveNumeric ||
            isZeroDetailFeeRow
        );
    }

    return isInclOrDash || hasPositiveNumeric || isZeroDetailFeeRow;
}

/** Same logic as Cart.vue hasSuppressedRowsInRunBreakdown */
export function hasSuppressedRowsInRunBreakdown(runChargeBreakdown) {
    if (!Array.isArray(runChargeBreakdown) || runChargeBreakdown.length === 0) {
        return false;
    }
    const isLocationBuckets =
        runChargeBreakdown[0] &&
        runChargeBreakdown[0].location &&
        Array.isArray(runChargeBreakdown[0].items);
    if (isLocationBuckets) {
        return runChargeBreakdown.some((bucket) =>
            (bucket.items || []).some(
                (row) =>
                    row &&
                    row.is_suppressed_by_group === true &&
                    parseFloat(row.total || 0) === 0
            )
        );
    }
    return runChargeBreakdown.some(
        (row) =>
            row &&
            row.is_suppressed_by_group === true &&
            parseFloat(row.total || 0) === 0
    );
}

/** Resolve decoration_type from order line (API shape varies). */
export function resolveItemDecorationType(item) {
    if (!item) return null;
    const candidates = [
        item.decoration_type,
        item.NewOrderItemDecoration &&
            item.NewOrderItemDecoration.decoration_type,
        item.decoration && item.decoration.decoration_type
    ];
    for (let i = 0; i < candidates.length; i++) {
        const raw = candidates[i];
        if (raw == null || raw === "") continue;
        const d = parseInt(raw, 10);
        if (!Number.isNaN(d)) return d;
    }
    return null;
}

export function itemUsesRunChargeDecoration(item) {
    const dec = resolveItemDecorationType(item);
    if (dec === 3) return false;
    if (dec != null && !Number.isNaN(dec)) {
        return dec === 0 || dec === 1 || dec === 2 || dec === 4 || dec === 5;
    }
    if (!item || item.decoration_type === undefined || item.decoration_type === null) {
        return false;
    }
    const dt = item.decoration_type;
    return (
        dt == 0 ||
        dt == 1 ||
        dt == "1" ||
        dt == 2 ||
        dt == 4 ||
        dt == 5
    );
}

/** Imprint suppress enabled with 2+ locations (same design group) — show $0 run charge even if breakdown missing on order row */
export function isSuppressMultiLocationRunChargeCase(item) {
    const suppressConfig = getSuppressAdditionalSetupRunChargeConfig(item);
    if (!suppressConfig.enabled) {
        return false;
    }
    const imprintLocs = getEffectiveImprintLocations(item);
    const selected = (imprintLocs || []).filter(
        (loc) => loc && loc.is_selected !== false
    );
    if (selected.length < 2) {
        return false;
    }
    const grouping = getLocationGroupingMetadata(imprintLocs, true);
    return grouping.some((entry) => entry && !entry.isGroupPrimary);
}

/**
 * Order details + cart: show Run Charge row (including $0.00 suppress case).
 */
export function shouldShowRunChargeLineForOrder(
    item,
    runChargeBreakdown,
    runChargeTotal = 0
) {
    if (parseFloat(runChargeTotal) > 0) {
        return true;
    }
    if (hasSuppressedRowsInRunBreakdown(runChargeBreakdown)) {
        return true;
    }
    if (itemUsesRunChargeDecoration(item)) {
        return true;
    }
    if (isSuppressMultiLocationRunChargeCase(item)) {
        return true;
    }
    return (
        Array.isArray(runChargeBreakdown) && runChargeBreakdown.length > 0
    );
}

/**
 * Order details / cart summary: show Run Charge row when total is $0 but breakdown has visible rows (e.g. suppressed).
 */
export function shouldShowRunChargeOrderLine(item, runChargeBreakdown) {
    const dec = resolveItemDecorationType(item);
    if (
        dec !== 0 &&
        dec !== 1 &&
        dec !== 2 &&
        dec !== 4 &&
        dec !== 5 &&
        !Number.isNaN(dec)
    ) {
        return false;
    }
    if (
        !runChargeBreakdown ||
        !Array.isArray(runChargeBreakdown) ||
        runChargeBreakdown.length === 0
    ) {
        return false;
    }
    let hasVisible = false;
    const decTypesWithRunCharge =
        dec === 0 || dec === 1 || dec === 2 || dec === 4 || dec === 5;

    runChargeBreakdown.forEach((breakdownItem) => {
        if (breakdownItem.items && Array.isArray(breakdownItem.items)) {
            breakdownItem.items.forEach((row) => {
                if (!row || row.type === "total") return;
                if (shouldShowRunChargeBreakdownRow(row, dec)) {
                    hasVisible = true;
                }
            });
        } else if (breakdownItem && breakdownItem.type !== "total") {
            if (shouldShowRunChargeBreakdownRow(breakdownItem, dec)) {
                hasVisible = true;
            }
        }
    });

    if (!hasVisible && decTypesWithRunCharge) {
        hasVisible = runChargeBreakdown.some((breakdownItem) => {
            if (
                breakdownItem.items &&
                Array.isArray(breakdownItem.items) &&
                breakdownItem.items.length > 0
            ) {
                return breakdownItem.items.some(
                    (row) => row && row.type !== "total"
                );
            }
            return (
                breakdownItem &&
                breakdownItem.type &&
                breakdownItem.type !== "total"
            );
        });
    }

    return hasVisible;
}

/**
 * @param {function(number): string} formatPriceFn — e.g. component formatPrice
 */
export function formatBreakdownMoneyForDisplay(formatPriceFn, val) {
    if (val === "INCL" || val === "-") return val;
    const n = parseFloat(
        val !== undefined && val !== null && val !== "" ? val : 0
    );
    const x = Number.isNaN(n) ? 0 : n;
    return formatPriceFn(x);
}
